import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';

interface Toast {
  id: number;
  text: string;
  tone?: 'bad';
  action?: { label: string; run: () => void };
}

type Push = (text: string, opts?: { tone?: 'bad'; action?: Toast['action']; ms?: number }) => void;

const Ctx = createContext<Push>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const next = useRef(1);

  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);

  const push = useCallback<Push>((text, opts = {}) => {
    const id = next.current++;
    setToasts((t) => [...t.slice(-2), { id, text, tone: opts.tone, action: opts.action }]);
    setTimeout(() => dismiss(id), opts.ms ?? (opts.action ? 6000 : 4000));
  }, [dismiss]);

  return (
    <Ctx.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast${t.tone === 'bad' ? ' bad' : ''}`}>
            <span>{t.text}</span>
            {t.action && (
              <button type="button" onClick={() => { dismiss(t.id); t.action!.run(); }}>{t.action.label}</button>
            )}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export const useToast = () => useContext(Ctx);
